import {KonvaEventObject} from 'konva/types/Node';
import {KonvaPointerEvent} from 'konva/types/PointerEvents';
import {Line, Circle, Group, Text, Rect, Label} from 'react-konva';
import React, {useState, useCallback, useEffect, useMemo, useRef} from 'react';

import {BOX_LABEL_DEFAULT, BOX_LABEL_SELECTED} from './const';
import {LabelPropsEvent, Point, PolyLabelProps} from './types';

function calculatePolygonCentroid(vertices: Point[]): Point {
  const vertexCount = vertices.length;
  let polygonArea = 0;
  let centroidX = 0;
  let centroidY = 0;

  for (let i = 0; i < vertexCount; i++) {
    const currentVertex = vertices[i];
    const nextVertex = vertices[(i + 1) % vertexCount];

    const crossProduct = currentVertex.x * nextVertex.y - nextVertex.x * currentVertex.y;

    polygonArea += crossProduct;
    centroidX += (currentVertex.x + nextVertex.x) * crossProduct;
    centroidY += (currentVertex.y + nextVertex.y) * crossProduct;
  }

  polygonArea /= 2;
  centroidX /= 6 * polygonArea;
  centroidY /= 6 * polygonArea;

  return {x: centroidX, y: centroidY};
}

type PolygonProps = {
  width?: number;
  height?: number;
  selected?: boolean;
  data: PolyLabelProps;
  onPointDrag?: (index: number, newPoint: Point) => void;
  onHover?: (event: KonvaEventObject<MouseEvent>) => void;
  onClick?: (event: KonvaEventObject<MouseEvent>) => void;
  onMouseMove?: (event: KonvaEventObject<MouseEvent>) => void;
  onMouseLeave?: (event: KonvaEventObject<MouseEvent>) => void;
  onRightClick?: (event: KonvaEventObject<PointerEvent>) => void;
};

export function Polygon({
  data,
  selected = false,
  onClick = () => {},
  onHover = () => {},
  onPointDrag = () => {},
  onMouseMove = () => {},
  onRightClick = () => {},
  onMouseLeave = () => {},
}: LabelPropsEvent & PolygonProps) {
  const textRef = useRef<any>(null);
  const lineRef = useRef<any>(null);
  const [isHover, setIsHover] = useState(false);
  const [labelStyle, setLabelStyle] = useState(BOX_LABEL_DEFAULT);
  const [labelDimensions, setLabelDimensions] = useState({width: 0, height: 0});

  const points = useMemo(() => data.points, [data.points]);
  const centroid = useMemo(() => calculatePolygonCentroid(points), [points]);

  const handleClick = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      event.evt.preventDefault();
      event.cancelBubble = true;

      onClick(event);
    },
    [onClick]
  );

  const handleRightClick = useCallback(
    (event: KonvaEventObject<PointerEvent>) => {
      event.evt.preventDefault();
      event.cancelBubble = true;

      onRightClick(event);
    },
    [onRightClick]
  );

  const onMouse = useCallback(
    (isHovering: boolean, event: KonvaEventObject<MouseEvent>) => {
      setIsHover(isHovering);

      if (isHovering) {
        onHover(event);
      } else {
        onMouseLeave(event);
      }
    },
    [onHover, onMouseLeave]
  );

  useEffect(() => {
    setLabelStyle(isHover || selected ? BOX_LABEL_SELECTED : BOX_LABEL_DEFAULT);
  }, [isHover, selected]);

  useEffect(() => {
    if (textRef.current) {
      const paddingWidth = 20;
      const paddingHeight = 10;
      const node = textRef.current;
      const width = node.getTextWidth();
      const height = node.getTextHeight();

      setLabelDimensions({width: width + paddingWidth, height: height + paddingHeight});
    }
  }, [data.label]);

  const handlePointDragStart = useCallback(
    (index: number, event: KonvaEventObject<DragEvent>) => {
      event.target.stopDrag();
    },
    []
  );

  const handlePointDragMove = useCallback(
    (index: number, event: KonvaEventObject<DragEvent>) => {
      const stage = event.target.getStage();

      if (stage) {
        const pointerPosition = stage.getPointerPosition();

        if (pointerPosition) {
          onPointDrag(index, pointerPosition);
        }
      }
    },
    [onPointDrag]
  );

  const handleMouseMove = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      if (onMouseMove) {
        onMouseMove(event);
      }
    },
    [onMouseMove]
  );

  const simulateLineEvent = useCallback(
    (event: KonvaEventObject<any>, callback?: (event: KonvaEventObject<any>) => void) => {
      if (lineRef.current) {
        event.target = lineRef.current;
        event.currentTarget = lineRef.current;

        if (callback) {
          callback(event);
        }
      }
    },
    []
  );

  const polygonInternalMouseEvents = useMemo(
    () => ({
      onClick: (originalEvent: KonvaEventObject<MouseEvent>) =>
        simulateLineEvent(originalEvent, handleClick),
      onContextMenu: (originalEvent: KonvaPointerEvent) =>
        simulateLineEvent(originalEvent, handleRightClick),
      onMouseMove: (originalEvent: KonvaEventObject<MouseEvent>) =>
        simulateLineEvent(originalEvent, handleMouseMove),
      onMouseEnter: (originalEvent: KonvaEventObject<MouseEvent>) =>
        simulateLineEvent(originalEvent, event => onMouse(true, event)),
      onMouseLeave: (originalEvent: KonvaEventObject<MouseEvent>) =>
        simulateLineEvent(originalEvent, event => onMouse(false, event)),
      onMouseDown: (originalEvent: KonvaEventObject<MouseEvent>) =>
        simulateLineEvent(originalEvent),
    }),
    [handleClick, handleRightClick, handleMouseMove, onMouse, simulateLineEvent]
  );

  return (
    <>
      <Group zIndex={selected ? 0 : 1}>
        <Line
          ref={lineRef}
          lineJoin="round"
          {...labelStyle}
          id={data.id}
          closed={true}
          name="polygon"
          strokeWidth={2}
          fill={selected ? '#00AEEF8A' : '#00AEEF40'}
          stroke={selected ? '#00AEEF8A' : '#00AEEF40'}
          points={points.flatMap(point => [point.x, point.y])}
          {...polygonInternalMouseEvents}
        />
      </Group>
      <Group zIndex={2}>
        {points.map((point, index) => (
          <Circle
            radius={4}
            key={index}
            x={point.x}
            y={point.y}
            draggable={true}
            fill={selected ? '#00AEEF8A' : '#00AEEF40'}
            onDragMove={event => handlePointDragMove(index, event)}
            onDragStart={event => handlePointDragStart(index, event)}
          />
        ))}
      </Group>
      <Group zIndex={3}>
        {!data.label ? null : (
          <Label
            x={centroid.x}
            y={centroid.y}
            name="polygon"
            offsetX={labelDimensions.width / 2}
            offsetY={labelDimensions.height / 2}
          >
            <Rect
              fill="#FFF2CB"
              cornerRadius={3}
              name="polygon"
              height={labelDimensions.height}
              width={labelDimensions.width + 4}
              {...polygonInternalMouseEvents}
            />
            <Text
              ref={textRef}
              padding={4}
              fontSize={16}
              fill="#000000"
              align="center"
              name="polygon"
              verticalAlign="middle"
              text={data.label.trim()}
              {...polygonInternalMouseEvents}
            />
          </Label>
        )}
      </Group>
    </>
  );
}
